<template>
  <div class="navbar-first-default">
    <div class="navbar-first-default-second">
      <ul
        class="navbar-second-level"
        :class="{ 'has-items-open': subtreeIndex !== -1 }"
      >
        <li v-for="(subLink, i) in itemsLarge" :key="i">
          <ZtLink
            :to="subLink.link.url.path"
            class="z-100"
            :class="[
              subLink.link.content.fieldTaxonomyLogoColor
                ? 'text-' +
                  getColor(subLink.link.content.fieldTaxonomyLogoColor)
                : 'default-color',
              subLink.link.content.fieldTaxonomyLogoColor
                ? 'hover:text-' +
                  getColor(subLink.link.content.fieldTaxonomyLogoColor)
                : '',
              subtreeIndex === i ? 'is-active' : '',
            ]"
            @touchend="
              handleClick(
                $event,
                i,
                subLink.subtree.length,
                subLink.link.url.path,
              )
            "
            @mouseenter="handleMouseEnter(i)"
          >
            <span>{{ subLink.link.label }}</span>
            <span
              v-if="subLink.subtree.length"
              class="navbar-arrow"
              :class="{
                ['text-' +
                getColor(subLink.link.content.fieldTaxonomyLogoColor)]:
                  subLink.link.content.fieldTaxonomyLogoColor &&
                  subtreeIndex === i,
              }"
              >➡</span
            >
          </ZtLink>
          <Transition name="flyout-third">
            <StorageMenuOldNavigationNavbarThird
              v-if="subLink.subtree.length"
              v-show="subtreeIndex === i"
              :links="subLink.subtree"
              :title="subLink.link.label"
              :teaser="
                subLink.link.content.fieldReferenceBlock
                  ? subLink.link.content.fieldReferenceBlock[0]
                  : undefined
              "
              :color="getColor(subLink.link.content.fieldTaxonomyLogoColor)"
            />
          </Transition>
        </li>
      </ul>
    </div>

    <StorageMenuOldNavigationNavbarThird
      :links="itemsSmall"
      :teaser="teasers[0]"
      :wysiwyg="wysiwyg"
    />
  </div>
</template>

<script lang="ts" setup>
import { COLOR_MAP } from '../../../../helper'
import { getPathFromUrl } from '~/helpers'
import { useSubtreeIndex } from '../../../../../../../composables/useOldMenu'
import type {
  BlockContentTeaserImageFragment,
  BlockContentWysiwygFragment,
} from '#build/graphql-operations'

const { isMobile } = useViewport()

const props = defineProps<{
  link: any
  subtree: any[]
  activeFirstLevel: number
}>()

const subtreeIndex = useSubtreeIndex()

const itemsSmall = computed(() => {
  return Object.values(props.subtree).filter((item) => {
    return item.link.content.viewMode !== undefined
      ? item.link.content.viewMode === 'default'
      : false
  })
})

const itemsLarge = computed(() => {
  return Object.values(props.subtree).filter((item) => {
    return item.link.content.viewMode !== undefined
      ? item.link.content.viewMode !== 'default'
      : true
  })
})

type PossibleBlock =
  | BlockContentWysiwygFragment
  | BlockContentTeaserImageFragment

const teasers = computed(() => {
  const blocks: PossibleBlock[] = props.link.content.fieldReferenceBlock || []
  return blocks.filter((item) => item.entityBundle === 'teaser_image')
})

const wysiwyg = computed(() => {
  const blocks = Object.values(
    props.link.content.fieldReferenceBlock || {},
  ) as BlockContentWysiwygFragment[]
  const items = blocks.filter((item) => item.entityBundle === 'wysiwyg')
  return items[0]?.body
})

function getColor(color: any) {
  if (color && color.tid) {
    return COLOR_MAP[color.tid]
  }
  return ''
}

function handleMouseEnter(index: number) {
  if (isMobile.value) {
    return
  }
  subtreeIndex.value = index
}

const router = useRouter()

function handleClick(
  event: Event,
  index: number,
  childLinksLength: number,
  url: string,
) {
  if (isMobile.value && childLinksLength > 0) {
    event.preventDefault()
    subtreeIndex.value = index
    return
  }

  const path = getPathFromUrl(url)
  event.preventDefault()
  router.push(path)
}

watch(
  () => props.activeFirstLevel,
  () => {
    subtreeIndex.value = -1
  },
)
</script>

<style lang="postcss">
.navbar-first-default {
  @apply belowDesktop:relative;
  @apply desktop:relative desktop:flex;

  @screen desktop {
    > .navbar-third {
      flex: 1;
    }
  }

  > .navbar-third {
    @apply belowDesktop:mt-size-6 belowDesktop:border-t belowDesktop:border-solid belowDesktop:border-border belowDesktop:pt-size-6;
  }
}

.navbar-first-default-second {
  @screen desktop {
    flex: 0 0 28rem;
  }
}

.navbar-second-level > li {
  > a {
    @apply flex justify-between text-size-4-5;
    @apply tablet:text-size-4;
    @apply widescreen:text-size-3;
    line-height: 1.5;

    @screen tablet {
      line-height: 1.25;
    }
    &.inactive {
      @apply !text-disabled;
    }
    &:not(.is-active) {
      &:hover {
        .navbar-arrow {
          @apply text-disabled;
        }
      }
      .navbar-arrow {
        @apply text-border;
      }
    }
  }
  > .navbar-third {
    @apply absolute bottom-0 bg-background;
    top: -1rem;
    right: -1rem;
    left: -1rem;
    padding: 1rem;
    z-index: 200;
    @screen desktop {
      left: 28rem;
      z-index: 90;
      padding-left: 2rem;
    }
  }
}

.navbar-second-level {
  > li > a.default-color {
    @apply text-scheme-light;
  }
}

.navbar-second-level.has-items-open {
  @screen desktop {
    > li > a:not(.is-active) {
      @apply text-disabled;
    }
    > li > a.default-color:not(.is-active) {
      @apply text-scheme-light;
    }
    > li > a.is-active.default-color {
      @apply text-scheme-invert;
    }
  }
}

.navbar-third-overlay {
  @apply fixed left-0 top-0 z-50 h-full w-full;
}

@screen belowDesktop {
  .flyout-third-enter-from {
    transform: translateX(100%);
  }
  .flyout-third-enter-active {
    transition: transform 0.5s ease;
  }
  .flyout-third-enter-to {
    transform: translateX(0);
  }

  .flyout-third-leave-from {
    transform: translateX(0);
  }
  .flyout-third-leave-active {
    transition: transform 0.5s ease;
  }
  .flyout-third-leave-to {
    transform: translateX(100%);
  }
}
</style>
